import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, action, toJS } from 'mobx';
import { Alert, Icon, Intent } from '@blueprintjs/core';
import moment from 'moment';
import Toast from 'helpers/toaster';
import { formatTime } from 'helpers/time';
import TechnicianPicker from 'components/TechnicianPicker';
import DateInput from 'components/DateInput';
import DateTimeInput from 'components/DateTimeInput';
import SendInvoiceModal from 'components/Payment/SendInvoiceModal';
import TransactionModal from 'components/Invoice/TransactionModal';
import ManualTransactionModal from 'components/Invoice/ManualTransactionModal';
import CardTransactionModal from 'components/Invoice/CardTransactionModal';
import SendReceiptDialog from 'components/Invoice/SendReceiptDialog';
import AddProductForInvoiceModal from 'components/Invoice/AddProductForInvoiceModal';
import QuickbooksSync from 'components/Invoice/QuickbooksSync';
import DocModel, { reason } from 'components/DocEditor/DocModel';
import LineItems from 'components/DocEditor/LineItems';
import RichText from 'components/DocEditor/RichText';
import { documentTotals, amount } from 'components/DocEditor/lineMath';
import {
  Toolbar, ToolButton, MoreMenu, Letterhead, DocTitle, ClientBlock, Meta, Totals, SideCard, SaveBar, SidePanel, BarFigures, Breakdown, NotesTabs, money,
} from 'components/DocEditor/DocParts';

// An invoice (pages/invoices/show.js), edited as the invoice the customer
// gets: who it's billed to, the dates, the lines with their taxes, what's
// been paid and what's due, and the notes and terms, stamped with whether
// it's paid. Payments sit below the paper; QuickBooks is in More. Same editor
// as work orders and estimates (components/DocEditor).

const isPaid = (a) => a.payment_status === 'paid';
const isOverdue = (a) => !isPaid(a) && a.invoice_due_date && moment(a.invoice_due_date).isBefore(moment(), 'day');

// The stamp: paid, part paid, overdue or unpaid (as the invoice list shows it).
export const paymentStamp = (a) => {
  let tone = 'grey';
  if (isPaid(a)) tone = 'green';
  else if (a.payment_status === 'partially_paid') tone = 'amber';
  if (isOverdue(a)) tone = 'red';
  return { tone, label: isOverdue(a) && !a.formatted_payment_status ? 'Overdue' : (a.formatted_payment_status || 'Unpaid') };
};

// Saved: the server's figures (what payments and the PDF use). With unsaved
// changes: the figures as they'll be once saved. Its own observer, so typing
// in a line doesn't re-render the whole invoice.
const InvoiceTotals = observer(({ doc }) => {
  const a = doc.attributes;
  const live = documentTotals(a);
  if (doc.dirty) {
    return (
      <Totals
        note={<><Breakdown doc={doc} /><p className="de-totals__hint">Totals include your unsaved changes.</p></>}
        rows={[
          { label: 'Subtotal', value: live.subtotal },
          { label: 'Tax', value: live.tax },
          { label: 'Total', value: live.grand, muted: live.paid > 0 },
          live.paid > 0 && { label: 'Paid', text: `−${money(live.paid)}`, muted: true },
          { label: live.paid > 0 ? 'Amount due' : 'Total due', value: live.due, strong: true },
        ]}
      />
    );
  }
  const grand = amount(a.grand_total);
  const due = amount(a.due_amount);
  const paid = grand - due;
  return (
    <Totals
      note={<Breakdown doc={doc} />}
      rows={[
        { label: 'Subtotal', value: amount(a.subtotal) },
        ...(a.taxes || []).filter((tax) => amount(tax.total) > 0).map((tax) => ({ label: `${tax.name} ${tax.tax_percentage_value}%`, value: amount(tax.total) })),
        { label: 'Total', value: grand, muted: paid > 0 },
        paid > 0 && { label: 'Paid', text: `−${money(paid)}`, muted: true },
        { label: paid > 0 ? 'Amount due' : 'Total due', value: due, strong: true },
      ]}
    />
  );
});

@inject('store', 'viewStore', 'invoiceStore', 'taxStore', 'quickbooksStore') @observer
class InvoiceEditor extends React.Component {
  @observable sendOpen = false;
  @observable pickerType = null;
  @observable paymentStep = null; // 'choose' | 'cash' | 'card'
  @observable receiptOpen = false;
  @observable deletingPayment = null;
  @observable saveFirst = null; // what to run once the changes are saved
  @observable busy = null;

  constructor(props) {
    super(props);
    const { invoiceStore, viewStore, taxStore } = props;
    const store = invoiceStore;
    const id = () => store.invoice.object.id;
    this.qb = { push: () => undefined, unlink: () => undefined };
    this.doc = new DocModel({
      kind: 'invoice',
      noun: 'invoice',
      features: { markup: false, override: false, materialOrders: false, taxes: true },
      fields: ['po_number', 'created_at', 'invoice_due_date', 'description', 'payment_terms', 'agreement_copy', 'private_notes'],
      getRecord: () => store.invoice.object,
      saveRecord: () => store.update(),
      reloadRecord: () => store.getInvoice({ uuid: id(), manual_transcation: true }),
      truckCharge: () => viewStore.currentUser && viewStore.currentUser.attributes && viewStore.currentUser.attributes.truck_charge,
      // As the old header did when it mounted: no due date means due on the invoice date.
      prepare: (attributes) => {
        if (!attributes.invoice_due_date) attributes.invoice_due_date = moment(attributes.created_at);
      },
    });
    const deleteTax = { labour: (taxLine) => store.deleteLabourTaxLineItem(taxLine), material: (taxLine) => store.deleteMaterialTaxLineItem(taxLine), custom: (taxLine) => store.deleteCustomTaxLineItem(taxLine) };
    Object.assign(this.doc, {
      deleteLine: (item) => store.deleteLineItem(item),
      addBlankLine: (kind) => store.addBlankLineItem(kind, id()),
      openPicker: (type) => { this.pickerType = type; },
      taxes: () => (taxStore.taxes && taxStore.taxes.objects) || [],
      setTax: (taxLine, tax) => taxStore.setTaxLineItem(taxLine, tax),
      canDeleteTax: () => true,
      // The server answers with the line's taxes; take it off here too.
      deleteTax: (taxLine, kind) => deleteTax[kind](taxLine).then(action((response) => {
        ['labour', 'materials', 'custom_line_items'].forEach((group) => {
          (this.attributes[group] || []).forEach((item) => {
            if ((item.tax_line_items || []).some((tax) => tax.id === taxLine.id)) {
              item.tax_line_items = item.tax_line_items.filter((tax) => tax.id !== taxLine.id);
            }
          });
        });
        return response;
      })),
    });
  }

  componentWillUnmount() {
    this.doc.dispose();
  }

  get record() {
    return this.props.invoiceStore.invoice.object;
  }

  get attributes() {
    return this.record.attributes;
  }

  run(name, work, success, failure) {
    this.busy = name;
    return work()
      .then((response) => { if (success) success(response); return response; })
      .catch((error) => { Toast.show({ message: reason(error, failure), intent: Intent.DANGER }); })
      .finally(action(() => { this.busy = null; }));
  }

  // Recording a payment reloads the invoice; with unsaved changes, ask first.
  whenSaved(next) {
    if (this.doc.dirty) this.saveFirst = next;
    else next();
  }

  confirmSaveFirst = () => {
    const next = this.saveFirst;
    this.saveFirst = null;
    this.run('save', () => this.doc.save(), () => next(), "Couldn't save your changes. Try again.");
  };

  markSent = () => {
    this.run('sent',
      () => this.doc.withSaved(() => this.props.invoiceStore.changeStatus('sent')),
      () => { Toast.show({ message: 'Marked as sent.', intent: Intent.SUCCESS }); },
      "Couldn't mark it as sent. Try again.");
  };

  changeTechnician = (selections) => {
    const { invoiceStore } = this.props;
    const current = this.attributes.created_by && this.attributes.created_by.id;
    const next = selections.map((option) => option.value).find((value) => value !== current);
    if (!next) return;
    this.run('technician',
      () => this.doc.withSaved(() => invoiceStore.changeTechnician(this.record, next)),
      (response) => {
        const person = response && response.data && response.data.data.attributes.created_by;
        Toast.show({ message: person ? `Technician changed to ${person.full_name}.` : 'Technician changed.', intent: Intent.SUCCESS });
      },
      "Couldn't change the technician. Try again.");
  };

  // Sending and the PDF use the saved invoice: save changes first.
  afterSave(name, next) {
    this.run(name, () => this.doc.withSaved(() => undefined), () => next(), "Couldn't save your changes. Try again.");
  }

  downloadPdf = () => {
    this.run('pdf', () => this.doc.withSaved(() => this.props.invoiceStore.downloadInvoicePDF()), null, "Couldn't download the PDF. Try again.");
  };

  copyLink = () => {
    const link = this.attributes.public_link;
    const clipboard = typeof navigator !== 'undefined' && navigator.clipboard;
    if (!link) return;
    if (!clipboard || !clipboard.writeText) {
      Toast.show({ message: "Couldn't copy. Open the customer's page and copy its address.", intent: Intent.WARNING });
      return;
    }
    clipboard.writeText(link)
      .then(() => { Toast.show({ message: 'Link copied.', intent: Intent.SUCCESS }); })
      .catch(() => { Toast.show({ message: "Couldn't copy. Open the customer's page and copy its address.", intent: Intent.WARNING }); });
  };

  recordPayment = () => this.whenSaved(() => { this.paymentStep = 'choose'; });

  markPaid = () => this.whenSaved(() => { this.paymentStep = 'cash'; });

  sendReceipt(payment) {
    this.props.invoiceStore.selectedTransaction = payment;
    this.receiptOpen = true;
  }

  deletePayment = () => {
    const payment = this.deletingPayment;
    this.deletingPayment = null;
    this.run('payment',
      () => this.props.invoiceStore.deleteTransaction(payment),
      () => { Toast.show({ message: 'Payment deleted.', intent: Intent.SUCCESS }); },
      "Couldn't delete the payment. Try again.");
  };

  renderToolbar() {
    const a = this.attributes;
    const client = a.client || {};
    return (
      <Toolbar
        actions={(
          <>
            <MoreMenu
              items={[
                { text: a.status === 'sent' ? 'Marked as sent' : 'Mark as sent', icon: 'tick', onClick: this.markSent, disabled: a.status === 'sent' || this.busy === 'sent' },
                { text: 'Mark as paid', icon: 'dollar', onClick: this.markPaid, disabled: isPaid(a) },
                a.public_link && { text: "Open the customer's page", icon: 'share', href: a.public_link },
                a.public_link && { text: 'Copy link', icon: 'link', onClick: this.copyLink },
                { text: this.busy === 'pdf' ? 'Downloading…' : 'Download PDF', icon: 'download', onClick: this.downloadPdf, disabled: this.busy === 'pdf' },
                { divider: true, title: 'QuickBooks' },
                { text: a.synced_with_quickbooks ? 'Update in QuickBooks' : 'Push to QuickBooks', icon: 'cloud-upload', onClick: () => this.qb.push() },
                client.synced_with_quickbooks && { text: 'Unlink client from QuickBooks', icon: 'disable', onClick: () => this.qb.unlink() },
              ]}
            />
            {!isPaid(a) && <ToolButton icon="dollar" onClick={this.recordPayment}>Record payment</ToolButton>}
            <ToolButton icon="envelope" primary busy={this.busy === 'send'} onClick={() => this.afterSave('send', () => { this.sendOpen = true; })}>Send invoice</ToolButton>
          </>
        )}
      >
        <span className="de-status-field">
          <span className="de-status-field__label">Payment</span>
          <span className={`dl-pill dl-pill--${paymentStamp(a).tone}`}>{paymentStamp(a).label}</span>
        </span>
        {a.status === 'sent' && <span className="dl-pill dl-pill--blue">Sent</span>}
        {a.synced_with_quickbooks && (
          <span className="de-synced" title={a.synced_at_with_quickbooks ? `Last synced ${a.synced_at_with_quickbooks}` : undefined}>
            <Icon icon="tick-circle" iconSize={12} /> In QuickBooks
          </span>
        )}
      </Toolbar>
    );
  }

  renderParty() {
    const { viewStore } = this.props;
    const a = this.attributes;
    const client = a.client || {};
    const address = (a.current_location && a.current_location.full_address) || (a.location && a.location.full_address);
    const job = a.job && a.job.number ? a.job : null;
    const tech = a.created_by;
    return (
      <div className="de-parties">
        <ClientBlock label="Bill to" client={client} address={address} href={client.id ? `/dashboard/clients/${client.id}/` : undefined} />
        <div className="de-meta">
          <Meta label="Invoice date">
            <div className="de-date">
              <DateInput placeholder="Invoice date" defaultValue={moment(a.created_at)} onChange={(date) => { a.created_at = moment(date); }} />
            </div>
          </Meta>
          <Meta label="Due">
            <div className="de-date">
              <DateTimeInput format="ddd, MMM D, YYYY" placeholder="Due date" defaultValue={a.invoice_due_date ? moment(a.invoice_due_date) : undefined} onChange={(date) => { a.invoice_due_date = moment(date); }} />
            </div>
          </Meta>
          <Meta label="PO number" htmlFor="de-po">
            <input id="de-po" className="de-field" placeholder="None" value={a.po_number || ''} onChange={(event) => { a.po_number = event.target.value; }} />
          </Meta>
          <Meta label="Technician">
            <div className="de-picker">
              <TechnicianPicker
                fill="true"
                selectedOptions={tech ? toJS([{ id: tech.id, label: tech.full_name, value: tech.id, selected: true }]) : []}
                onChange={this.changeTechnician}
                includeAdmins={true}
                hideHeading={true}
              />
            </div>
          </Meta>
          {job && (
            <Meta label="Job">
              <a className="de-strong-link" href={`/dashboard/jobs/${job.id}/${(viewStore.jobTab && viewStore.jobTab.section) || 'details'}`}>#{job.number}</a>
              {job.title && <span className="de-muted">{job.title}</span>}
            </Meta>
          )}
        </div>
      </div>
    );
  }

  renderPayments() {
    const payments = this.attributes.transactions || [];
    return (
      <SideCard
        title="Payments"
        count={payments.length}
        action={!isPaid(this.attributes) && <ToolButton icon="dollar" onClick={this.recordPayment}>Record payment</ToolButton>}
      >
        {payments.length === 0 ? (
          <p className="de-muted">No payments yet. Record a cash, cheque or card payment when it comes in.</p>
        ) : (
          <ul className="de-payments">
            {payments.map((payment) => (
              <li key={payment.id} className="de-payment">
                <div className="de-payment__main">
                  <strong>{money(amount(payment.amount_paid))}</strong>
                  <span className="de-muted">{[payment.payment_method, payment.transaction_date && formatTime(payment.transaction_date)].filter(Boolean).join(', ')}</span>
                  {(payment.description || payment.notes) && <span className="de-muted">{[payment.description, payment.notes].filter(Boolean).join(' – ')}</span>}
                </div>
                <span className={`dl-pill dl-pill--${payment.status === 'paid' ? 'green' : payment.status === 'failed' ? 'red' : 'grey'}`}>{payment.status ? payment.status.charAt(0).toUpperCase() + payment.status.slice(1) : 'Recorded'}</span>
                <span className="de-payment__actions">
                  <button type="button" className="de-icon-button" aria-label="Send receipt" title="Send receipt" onClick={() => this.sendReceipt(payment)}>
                    <Icon icon="envelope" iconSize={14} />
                  </button>
                  {payment.is_manual && (
                    <button type="button" className="de-icon-button de-icon-button--danger" aria-label="Delete this payment" title="Delete" onClick={() => { this.deletingPayment = payment; }}>
                      <Icon icon="trash" iconSize={14} />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </SideCard>
    );
  }

  render() {
    const doc = this.doc;
    const a = this.attributes;
    return (
      <div className="de de--invoice">
        {this.renderToolbar()}

        <article className="de-sheet" aria-labelledby="de-title">
          <Letterhead tenant={a.tenant} />
          <DocTitle kind="Invoice" number={a.number} stamp={paymentStamp(a)} />
          {this.renderParty()}

          <LineItems doc={doc} />

          <InvoiceTotals doc={doc} />

          <NotesTabs
            kind="invoice"
            attributes={a}
            RichTextEditor={RichText}
            tabs={[
              { field: 'description', label: 'Notes for the customer', note: 'Shown to the customer on the invoice', placeholder: 'Thanks for your business, warranty details, anything they should know' },
              { field: 'payment_terms', label: 'Payment terms', note: 'Shown to the customer on the invoice', placeholder: 'When and how to pay, e.g. due on receipt by e-transfer or card' },
              { field: 'agreement_copy', label: 'Agreement', note: 'Shown to the customer on the invoice', placeholder: 'Terms that apply to this work' },
              { field: 'private_notes', label: 'Private notes', private: true, placeholder: "Notes for your team: access codes, follow-ups, anything the customer shouldn't see" },
            ]}
          />
        </article>

        <SidePanel kind="invoice">
          {this.renderPayments()}
        </SidePanel>

        <SaveBar
          doc={doc}
          figures={(
            <BarFigures
              doc={doc}
              // Saved: the server's figures (what payments use); with unsaved
              // changes, the live ones (as on the sheet).
              build={(totals, model) => {
                const a = model.attributes;
                const grand = model.dirty ? totals.grand : amount(a.grand_total);
                const due = model.dirty ? totals.due : amount(a.due_amount);
                return [
                  { label: 'Total', value: grand },
                  { label: 'Paid', value: grand - due },
                  { label: 'Amount due', value: due, strong: true },
                ];
              }}
            />
          )}
        />

        <QuickbooksSync register={(api) => { this.qb = api; }} before={() => (this.doc.dirty ? this.doc.save() : Promise.resolve())} />
        <SendInvoiceModal isOpen={this.sendOpen} onClose={() => { this.sendOpen = false; }} onSuccess={() => { this.sendOpen = false; }} />
        <TransactionModal
          isOpen={this.paymentStep === 'choose'}
          onClose={() => { this.paymentStep = null; }}
          onSuccess={(type) => { this.paymentStep = type === 'card' ? 'card' : 'cash'; }}
        />
        <ManualTransactionModal isOpen={this.paymentStep === 'cash'} onClose={() => { this.paymentStep = null; }} onSuccess={() => { this.paymentStep = null; }} />
        <CardTransactionModal isOpen={this.paymentStep === 'card'} onClose={() => { this.paymentStep = null; }} onSuccess={() => { this.paymentStep = null; }} />
        <SendReceiptDialog isOpen={this.receiptOpen} onClose={() => { this.receiptOpen = false; }} />
        <AddProductForInvoiceModal
          isOpen={Boolean(this.pickerType)}
          type={this.pickerType || 'Service'}
          itemType={this.pickerType === 'Service' ? 'Labor' : 'Material'}
          workDetail={this.record}
          onClose={() => { this.pickerType = null; }}
        />
        <Alert
          isOpen={Boolean(this.deletingPayment)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete payment"
          onCancel={() => { this.deletingPayment = null; }}
          onConfirm={this.deletePayment}
        >
          <strong>Delete this {this.deletingPayment ? money(amount(this.deletingPayment.amount_paid)) : ''} payment?</strong>
          <p>The amount goes back onto what's due{doc.dirty ? ', and your other changes are saved with it' : ''}.</p>
        </Alert>
        <Alert
          isOpen={Boolean(this.saveFirst)}
          icon="floppy-disk"
          intent={Intent.PRIMARY}
          cancelButtonText="Cancel"
          confirmButtonText="Save and continue"
          onCancel={() => { this.saveFirst = null; }}
          onConfirm={this.confirmSaveFirst}
        >
          <strong>Save your changes first?</strong>
          <p>Recording a payment reloads the invoice, so your changes are saved before it opens.</p>
        </Alert>
      </div>
    );
  }
}

export default InvoiceEditor;
